import { ComponentItem, RichTextType } from '~types'
import parse, { HTMLReactParserOptions, Element, attributesToProps, domToReact } from 'html-react-parser'
import { BASE_URLS, EVENT_SIZE, EVENT_SORT_BY, VENUE_ID } from '~constants'

const disableBodyScroll = (modalOpen: boolean) => {
  const html = document.querySelector('html')
  const body = document.querySelector('body')

  if (!html || !body) return

  if (modalOpen) {
    html.style.overflowY = 'hidden'
    body.style.overflowY = 'hidden'
    // required for iOS
    html.style.touchAction = 'none'
    body.style.touchAction = 'none'
  } else {
    html.style.overflowY = 'auto'
    body.style.overflowY = 'auto'
    html.style.touchAction = 'auto'
    body.style.touchAction = 'auto'
  }
}

const objHasValue = <T extends Record<string, unknown>, K extends keyof T>(obj: T, key: K, value: T[K]): boolean => {
  return Object.prototype.hasOwnProperty.call(obj, key) && obj[key] === value
}

const isTouchDevice = () => {
  if (typeof window !== 'undefined') return navigator.maxTouchPoints > 0
}

const isPastDeadline = (deadline: string) => {
  const now = new Date()
  const parsedDeadline = new Date(deadline)

  return now.getTime() >= parsedDeadline.getTime()
}

const parseHTMLString = (text: RichTextType): string | JSX.Element | JSX.Element[] => {
  const options: HTMLReactParserOptions = {
    replace: (domNode: any) => {
      const { type, name, attribs, children } = domNode
      if (domNode instanceof Element && type === 'tag' && name === 'a') {
        const props = attributesToProps(attribs)
        if (props && props.href && props.href.startsWith('/')) {
          const [anchor] = children
          const anchorLabel = anchor?.data
          const requestCameFromUrl = props.href
          const updatedProps = {
            ...props,
            href: requestCameFromUrl
          }
          return <a {...updatedProps}>{anchorLabel}</a>
        }
      }
    }
  }

  return parse(text?.html ?? '', options)
}

const isValidURL = (url: string) => {
  try {
    new URL(url)
    return true
  } catch (error) {
    return false
  }
}

const generateEventEngineBasePath = () => {
  return `${process.env.NEXT_PUBLIC_EVENT_ENGINE_API}?sort_by=${EVENT_SORT_BY}&size=${EVENT_SIZE}&venue_id=${VENUE_ID}`
}

const getArtistDetailsArray = (filteredArtists: any, artistDetails: any) => {
  return (filteredArtists ?? [])
    .map((artist: any | null) => {
      const artistId = artist ?? null
      const artistImageObject = artistId && artistDetails ? artistDetails[artistId] : null
      if (artistImageObject) {
        return {
          artistId: artistId,
          artistName: artistImageObject?.artistName || null,
          artistEDPUrl: artistImageObject?.edpUrl || null,
          artistImageUrl: artistImageObject?.image ? artistImageObject?.image?.url : null,
          artistImageAlt: artistImageObject?.image?.altText || null
        }
      }
      return null
    })
    .filter((item: any | null) => item !== null)
}

const isExternalLink = (url: string | null) => {
  if (!url) return false

  const baseSiteUrl = process.env.NEXT_PUBLIC_SITE_URL || BASE_URLS.prod
  try {
    const parsedUrl = new URL(url, baseSiteUrl)
    return parsedUrl.origin !== new URL(baseSiteUrl).origin
  } catch {
    return false
  }
}

const getFevoWidgetNameFromUrl = (url: string) => {
  try {
    // Remove query strings and hash fragments
    const cleanUrl = url.split('?')[0].split('#')[0]

    // Check if url contains gofevo or fevo-enterprise
    if (cleanUrl.includes('gofevo') || cleanUrl.includes('fevo-enterprise')) {
      const parts = cleanUrl.replace(/\/$/, '').split('/')

      // Return the last non-empty part
      return parts[parts.length - 1] || null
    }
    return null
  } catch (e) {
    return null
  }
}

const openFevoWidget = (widgetName: string, e: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
  // @ts-ignore
  GMWidget.open(widgetName)
  e.preventDefault()
}

const getPortalRoot = () => {
  if (typeof document === 'undefined') return null

  let portalRoot = document.getElementById('portal-root')

  if (!portalRoot) {
    portalRoot = document.createElement('div')
    portalRoot.id = 'portal-root'
    document.body.appendChild(portalRoot)
  }

  return portalRoot
}

const isComponentOfType = <T extends ComponentItem>(item: ComponentItem, type: string): item is T => {
  return 'componentName' in item && item.componentName === type
}

const hasComponentOfType = <T extends ComponentItem>(items: ComponentItem[], type: string): boolean => {
  return Array.isArray(items) && items.some(item => 'componentName' in item && item.componentName === type)
}

export const filterComponentsOfType = <T extends ComponentItem>(items: ComponentItem[], type: string): T[] =>
  items.filter((item): item is T => isComponentOfType<T>(item, type))

// Helper to strip a wrapping <p> tag if it's the only element
const unwrapSingleParagraph = (html: string) => {
  let onlyP = false

  const dom = parse(html, {
    replace: node => {
      if (node instanceof Element && node.name === 'p' && node.parent === null) {
        onlyP = true
        return <>{domToReact(node.children)}</>
      }
      return undefined
    }
  })

  // If it was just a <p> at root, it will be unwrapped
  return onlyP ? dom : parse(html)
}

// Normalize url path, stripping query strings, hashes and trailing slashes
const normalizePath = (path: string) => {
  return path.replace(/[?#].*$/, '').replace(/\/+$/, '') || '/'
}

const isValidEmail = (email: string) => {
  const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
  return regex.test(email)
}

const isStudiosProxyPage = () => {
  if (typeof window !== 'undefined') {
    const currentHost = window.location.host
    const studiosHost = new URL(BASE_URLS.studios).host

    return currentHost === studiosHost
  }
}

export {
  disableBodyScroll,
  getFevoWidgetNameFromUrl,
  getPortalRoot,
  normalizePath,
  objHasValue,
  openFevoWidget,
  parseHTMLString,
  isTouchDevice,
  isPastDeadline,
  isValidEmail,
  generateEventEngineBasePath,
  getArtistDetailsArray,
  isExternalLink,
  isComponentOfType,
  isStudiosProxyPage,
  hasComponentOfType,
  unwrapSingleParagraph
}
